import type { FinalScore } from '@/game/types';
import type { ChallengeView } from '@/net/api';
import { CHALLENGE_ANCHOR, type CardOutcome } from '@/share/layout';

/**
 * "Challenge Me": the settled challenge screen's Share button and the profile deep link its card's QR lands
 * on. Pure (no DOM), so the rules are tested in node; `screens.ts` draws the button and the card.
 *
 * Blockari's `shareable()` and Profile anchor (6d12ba3), on this stack.
 */

export { CHALLENGE_ANCHOR };

/**
 * Whether the settled screen offers Share: a challenge the viewer PLAYED that has a winner — won, lost or
 * decided by forfeit, staked or free. Not an open, taken or pending one (nothing to brag yet), and not a
 * voided or expired one (no winner: both stakes came back, or nobody ever sat down) nor a dead heat.
 * The server asks the same question (`isSettledForShare`) and answers 409 to the rest.
 */
export function shareable(view: ChallengeView): boolean {
  return view.result !== null && !view.voided && view.me !== null && view.opponent !== null;
}

/** The brag is primary when the viewer won it, and the plain button after a loss. */
export function shareIsPrimary(view: ChallengeView): boolean {
  return view.result?.won === true;
}

/** What the card is made of, from the viewer's own settled view. Null when it is not shareable. */
export function challengeMeCard(view: ChallengeView): { final: FinalScore; outcome: CardOutcome } | null {
  if (!shareable(view) || !view.me || !view.opponent || !view.result) return null;
  const me = view.me;
  const won = view.result.won;
  const final: FinalScore = {
    base: 0,
    perfectGameBonus: 0,
    noBustBonus: 0,
    timeBonus: 0,
    total: me.score,
    busts: 0,
    secondsRemaining: 0,
    cardsRemaining: 0,
    perfectGame: false,
    reason: me.reason ?? 'timeout',
    clears: me.clears ?? 0,
    bestStreak: me.bestStreak ?? 0,
    plays: 0,
    levelReached: me.level,
  };
  const outcome: CardOutcome = {
    won,
    // The prize on a win, the stake on a loss: what the round moved, as the results card says it.
    chain: won ? view.result.payout : view.entryFee,
    opponent: view.opponent.name,
    theirScore: view.opponentForfeited === true || view.opponent.score === null ? null : view.opponent.score,
  };
  return { final, outcome };
}

/** `/profile/<name>` (what a Challenge Me QR carries): the player's name, or null for any other path. */
export function profileFromPath(path: string): string | null {
  const m = /^\/profile\/([^/]+)\/?$/.exec(path);
  if (!m) return null;
  try {
    const name = decodeURIComponent(m[1]!);
    // The server decides whether such a player exists; this only keeps nonsense out of the request.
    return name.length >= 1 && name.length <= 64 && !/[\s/\\?#]/.test(name) ? name : null;
  } catch {
    return null;
  }
}

/** Whether a URL hash asks for the profile's challenge card. */
export function wantsChallengeCard(hash: string): boolean {
  return hash === `#${CHALLENGE_ANCHOR}`;
}
